import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { loyaltyApi } from '../services/loyaltyApi';
import { appLoginPath, appPath } from '../routes/appPaths';
import { useUserStore } from '../store/userStore';
import {
  canRequestTelegramContact,
  getTelegramInitData,
  openTelegramBot,
  requestTelegramContact,
} from '../telegram/telegramWebApp';

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

export function TelegramAuthGate({ botUsername }: { botUsername: string | null }) {
  const login = useUserStore((s) => s.login);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const canContact = canRequestTelegramContact();

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const initData = getTelegramInitData();
      if (!initData) return;
      try {
        const s = await loyaltyApi.telegramSession(initData);
        if (cancelled || !s.linked) return;
        login({
          phone: s.user.phone,
          userId: s.user.id,
          token: s.token,
          firstName: s.user.firstName,
          lastName: s.user.lastName,
        });
      } catch {
        /* ждём контакт в боте */
      }
    };
    const id = window.setInterval(tick, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [login]);

  const applySession = async () => {
    const initData = getTelegramInitData();
    if (!initData) return false;
    const deadline = Date.now() + 20_000;
    while (Date.now() < deadline) {
      const s = await loyaltyApi.telegramSession(initData);
      if (s.linked) {
        login({
          phone: s.user.phone,
          userId: s.user.id,
          token: s.token,
          firstName: s.user.firstName,
          lastName: s.user.lastName,
        });
        return true;
      }
      await sleep(600);
    }
    return false;
  };

  const onShare = async () => {
    setError('');
    setBusy(true);
    try {
      const sent = await requestTelegramContact();
      if (!sent) {
        setError('Номер не отправлен. Можно открыть бота и поделиться там.');
        return;
      }
      const ok = await applySession();
      if (!ok) {
        setError('Бот ещё не получил номер. Откройте чат и нажмите «Поделиться номером».');
      }
    } catch {
      setError('Не удалось запросить номер.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col justify-end px-5"
      style={{
        background: '#0a0a0a',
        paddingTop: 'calc(24px + var(--tg-safe-area-inset-top, 0px))',
        paddingBottom: 'calc(28px + var(--tg-safe-area-inset-bottom, 0px))',
      }}
    >
      <div className="max-w-md w-full mx-auto">
        <div
          className="text-[11px] tracking-[0.32em] uppercase mb-3"
          style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}
        >
          THEORY
        </div>
        <h1
          className="text-[2.4rem] leading-[0.95] font-bold uppercase mb-4"
          style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.04em', color: '#fff' }}
        >
          Войдите
        </h1>
        <p
          className="text-[15px] leading-relaxed mb-8"
          style={{ color: 'rgba(255,255,255,0.62)', fontFamily: 'Manrope, sans-serif' }}
        >
          Чтобы пользоваться приложением и картой лояльности, поделитесь номером телефона.
        </p>

        {canContact && (
          <button
            type="button"
            disabled={busy}
            onClick={onShare}
            className="w-full h-12 rounded-full text-[13px] tracking-[0.16em] uppercase font-semibold disabled:opacity-50"
            style={{ background: '#fff', color: '#0a0a0a', fontFamily: 'Manrope, sans-serif' }}
          >
            {busy ? (
              <Loader2 className="inline w-5 h-5 animate-spin" />
            ) : (
              'Поделиться номером'
            )}
          </button>
        )}

        {botUsername && (
          <button
            type="button"
            disabled={busy}
            onClick={() => openTelegramBot(botUsername)}
            className="w-full h-12 rounded-full mt-3 text-[13px] tracking-[0.16em] uppercase font-semibold disabled:opacity-50"
            style={{
              background: canContact ? 'transparent' : '#fff',
              color: canContact ? '#fff' : '#0a0a0a',
              border: canContact ? '1px solid rgba(255,255,255,0.22)' : 'none',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            Открыть бота
          </button>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={() => navigate(appLoginPath(appPath('/home')))}
          className="w-full h-12 mt-3 text-[12px] tracking-[0.14em] uppercase font-semibold disabled:opacity-50"
          style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}
        >
          Войти по коду
        </button>

        {error ? (
          <p
            className="mt-4 text-[13px] leading-relaxed"
            style={{ color: '#e8b4b4', fontFamily: 'Manrope, sans-serif' }}
          >
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
